import { useState } from 'react';
import { layToken, tien } from '../chung/api';
import { useHoiLai } from '../chung/hoi-lai';
import type { BaoCaoDoanhThu, BaoCaoNangSuat, VaiTro } from '../chung/kieu';
import { DaiLoi, Khung } from './manh';

const TEN_VAI: Record<VaiTro, string> = {
  SELLER: 'Tư vấn',
  CASHIER: 'Thu ngân',
  ADMIN: 'Quản lý',
  WAREHOUSER: 'Kho',
};

const nganNgay = (d: Date) => {
  const b = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return b.toISOString().slice(0, 10);
};

/**
 * Báo cáo — doanh thu theo ngày và năng suất theo người.
 *
 * 🔴 Doanh thu cộng dồn SỔ CÁI SỰ KIỆN, không cộng tổng tiền trên đơn. Một đơn
 *    có thể xác nhận → trả lại → xác nhận lại nhiều vòng; cộng trên đơn thì sau
 *    hai vòng không còn biết tiền đã đi thế nào. Đây là việc của backend, nhưng
 *    ghi lại ở đây để không ai "sửa cho nhanh" bằng cách cộng trên đơn.
 */
export function MucBaoCao() {
  const homNay = new Date();
  const [tu, datTu] = useState(nganNgay(new Date(homNay.getTime() - 29 * 86400000)));
  const [den, datDen] = useState(nganNgay(homNay));

  const khoang = `tu=${tu}&den=${den}`;
  const dt = useHoiLai<BaoCaoDoanhThu>(tu <= den ? `/api/bao-cao/doanh-thu?${khoang}` : null, 30_000);
  const ns = useHoiLai<BaoCaoNangSuat>(tu <= den ? `/api/bao-cao/nang-suat?${khoang}` : null, 30_000);

  return (
    <Khung
      tieuDe="Báo cáo"
      phu="Theo ngày làm việc (múi giờ Việt Nam), không theo giờ UTC"
      hanhDong={<NutXuatExcel tu={tu} den={den} choPhep={tu <= den} />}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginBottom: 20 }}>
        <label style={{ flex: 1 }}>
          <span className="nhan-chu">Từ ngày</span>
          <input type="date" value={tu} onChange={(e) => datTu(e.target.value)} style={{ marginTop: 7 }} />
        </label>
        <label style={{ flex: 1 }}>
          <span className="nhan-chu">Đến ngày</span>
          <input type="date" value={den} onChange={(e) => datDen(e.target.value)} style={{ marginTop: 7 }} />
        </label>
        <div style={{ display: 'flex', gap: 9 }}>
          {[
            { ten: 'Hôm nay', n: 0 },
            { ten: '7 ngày', n: 6 },
            { ten: '30 ngày', n: 29 },
          ].map((k) => (
            <button
              key={k.ten}
              onClick={() => {
                datTu(nganNgay(new Date(Date.now() - k.n * 86400000)));
                datDen(nganNgay(new Date()));
              }}
              style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}
            >
              {k.ten}
            </button>
          ))}
        </div>
      </div>

      {tu > den && (
        <div className="lom-nho" style={{ padding: '12px 16px', color: 'var(--nhan)', fontWeight: 600 }}>
          Ngày bắt đầu phải trước ngày kết thúc.
        </div>
      )}

      <DaiLoi loi={dt.loi ?? ns.loi} />

      {/* ── doanh thu ── */}
      <div className="lom" style={{ padding: '20px 22px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap' }}>
          <ONho nhan="Doanh thu" chu={dt.duLieu ? tien(dt.duLieu.tong) : '—'} to />
          <ONho nhan="Lần thu tiền" chu={dt.duLieu ? String(dt.duLieu.tong_lan_thu) : '—'} />
          <ONho
            nhan="Lần trả lại"
            chu={dt.duLieu ? String(dt.duLieu.tong_lan_tra_lai) : '—'}
            mau={dt.duLieu && dt.duLieu.tong_lan_tra_lai > 0 ? 'var(--nhan)' : undefined}
          />
        </div>
      </div>

      <BieuDo tu={tu} den={den} dt={dt.duLieu ?? null} dangTai={!dt.duLieu} />

      {/* ── năng suất ── */}
      <div className="nhan-chu" style={{ margin: '24px 4px 12px' }}>
        Năng suất từng người
      </div>

      {!ns.duLieu && <div className="xuong" style={{ height: 90 }} />}

      {ns.duLieu && ns.duLieu.danh_sach.length === 0 && (
        <div style={{ padding: '30px 10px', textAlign: 'center', color: 'var(--chu-3)' }}>
          Chưa có đơn nào đã thu tiền trong khoảng này.
        </div>
      )}

      {(ns.duLieu?.danh_sach ?? []).map((d, i) => (
        <div
          key={d.id}
          className="noi-nho truot-phai"
          style={{
            animationDelay: `${Math.min(i, 8) * 30}ms`,
            padding: '14px 18px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>{d.ho_ten}</span>
              <span className="the the-mo">{TEN_VAI[d.vai_tro]}</span>
            </div>
          </div>
          <ONho nhan="Đơn" chu={String(d.so_don)} />
          <ONho nhan="Món" chu={String(d.so_mon)} />
          <div style={{ minWidth: 130, textAlign: 'right' }}>
            <div className="nhan-chu" style={{ fontSize: 10 }}>
              Doanh số
            </div>
            <div className="tien" style={{ fontSize: 17, marginTop: 2 }}>
              {tien(d.doanh_so)}
            </div>
          </div>
        </div>
      ))}

      {ns.duLieu && (
        <div
          className="lom-nho"
          style={{ padding: '13px 16px', marginTop: 14, fontSize: 12, color: 'var(--chu-2)', lineHeight: 1.7 }}
        >
          {ns.duLieu.ghi_chu}
        </div>
      )}
    </Khung>
  );
}

function ONho({ nhan, chu, to, mau }: { nhan: string; chu: string; to?: boolean; mau?: string }) {
  return (
    <div style={{ minWidth: 72 }}>
      <div className="nhan-chu" style={{ fontSize: 10 }}>
        {nhan}
      </div>
      <div
        className={to ? 'tien' : 'so'}
        style={{ fontSize: to ? 26 : 17, fontWeight: to ? 800 : 600, color: mau, marginTop: 3 }}
      >
        {chu}
      </div>
    </div>
  );
}

/**
 * Cột theo ngày — vẽ bằng div, không kéo thư viện biểu đồ về.
 *
 * Ở đây chỉ cần thấy hình dáng của khoảng: ngày nào cao, ngày nào trũng. Một thư
 * viện biểu đồ cho việc này là thêm vài trăm KB và một thứ nữa phải nâng cấp.
 *
 * 🔴 PHẢI TỰ ĐIỀN NHỮNG NGÀY BACKEND KHÔNG TRẢ VỀ.
 *
 *    `/api/bao-cao/doanh-thu` chỉ trả những ngày CÓ dữ liệu. Hỏi khoảng 30 ngày
 *    mà cửa hàng chỉ bán một hôm thì nó trả đúng **một** phần tử.
 *
 *    Bản đầu vẽ thẳng mảng đó, và hai chuyện xảy ra cùng lúc — nhìn ảnh chụp mới
 *    thấy, đọc code thì không:
 *      · một cột duy nhất với `flex-grow: 1` **giãn kín cả chiều ngang**, ra một
 *        khối hồng đặc chẳng nói lên điều gì;
 *      · và tệ hơn: biểu đồ **nói dối về khoảng thời gian**. Ngày doanh thu 0
 *        biến mất khỏi trục thay vì hiện thành cột thấp, nên một tháng ế trông
 *        y hệt một tháng bán đều.
 *
 *    Nên trải đủ `tu`..`den` ra rồi mới ghép dữ liệu vào.
 */

/** Trải mọi ngày trong khoảng, ghép doanh thu vào, ngày thiếu cho về 0. */
function traiNgay(
  tu: string,
  den: string,
  co: BaoCaoDoanhThu['theo_ngay'],
): BaoCaoDoanhThu['theo_ngay'] {
  const bang = new Map(co.map((d) => [d.ngay, d]));
  const ra: BaoCaoDoanhThu['theo_ngay'] = [];
  // Đi bằng giờ UTC giữa trưa: cộng ngày kiểu này không bị lệch vì đổi giờ hay
  // vì múi giờ của máy đang mở màn hình.
  const d = new Date(`${tu}T12:00:00Z`);
  const cuoi = new Date(`${den}T12:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(cuoi.getTime())) return co;
  // Chặn 400 ngày: hỏi khoảng vô lý thì thà trả nguyên bản còn hơn treo vòng lặp.
  for (let i = 0; d <= cuoi && i < 400; i++) {
    const ma = d.toISOString().slice(0, 10);
    ra.push(bang.get(ma) ?? { ngay: ma, doanh_thu: 0, so_lan_thu: 0, so_lan_tra_lai: 0 });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return ra;
}

/** Một cột trên biểu đồ — đã gộp hai chế độ giờ và ngày về cùng một hình dạng. */
type Cot = {
  khoa: string;
  nhan: string;
  chuThich: string;
  doanh_thu: number;
};

/**
 * Biểu đồ doanh thu — **24 cột giờ** khi chọn đúng một ngày, ngược lại mỗi ngày
 * một cột (7 ngày ra 7 cột, 30 ngày ra 30 cột).
 *
 * 🔴 Vì sao một ngày phải đổi sang cột giờ: chọn "Hôm nay" mà vẽ theo ngày thì
 *    biểu đồ còn đúng MỘT cột, và cột đó luôn cao 100% vì nó cũng chính là đỉnh.
 *    Nó không nói được gì ngoài "có bán" — trong khi thứ cửa hàng muốn biết là
 *    giờ nào đông.
 *
 * 🔴 Cột **canh giữa** chứ không kéo giãn kín chiều ngang. Cột giãn theo bề rộng
 *    màn hình thì cùng một ngày mở trên hai máy ra hai hình khác nhau, và mắt
 *    quen dần với hình dáng của màn quen chứ không phải của số liệu. `maxWidth`
 *    ghim bề rộng lại, phần thừa chia đều hai bên.
 */
function BieuDo({
  tu,
  den,
  dt,
  dangTai,
}: {
  tu: string;
  den: string;
  dt: BaoCaoDoanhThu | null;
  dangTai: boolean;
}) {
  if (dangTai || !dt) return <div className="xuong" style={{ height: 170 }} />;

  const theoGio = tu === den ? dt.theo_gio : undefined;

  // Chế độ giờ: backend trả sẵn đủ 24 khay nên không phải tự trải.
  // Chế độ ngày: PHẢI tự trải — backend chỉ trả ngày CÓ dữ liệu.
  const cot: Cot[] = theoGio
    ? theoGio.map((g) => ({
        khoa: `g${g.gio}`,
        nhan: `${g.gio}h`,
        chuThich: `${String(g.gio).padStart(2, '0')}:00–${String(g.gio).padStart(2, '0')}:59 · ${tien(g.doanh_thu)} · ${g.so_lan_thu} lần thu${g.so_lan_tra_lai ? ` · ${g.so_lan_tra_lai} trả lại` : ''}`,
        doanh_thu: g.doanh_thu,
      }))
    : traiNgay(tu, den, dt.theo_ngay).map((d) => ({
        khoa: d.ngay,
        nhan: `${d.ngay.slice(8, 10)}/${d.ngay.slice(5, 7)}`,
        chuThich: `${d.ngay} · ${tien(d.doanh_thu)} · ${d.so_lan_thu} lần thu${d.so_lan_tra_lai ? ` · ${d.so_lan_tra_lai} trả lại` : ''}`,
        doanh_thu: d.doanh_thu,
      }));

  if (cot.length === 0) return null;

  const dinh = Math.max(...cot.map((c) => c.doanh_thu), 1);
  const coTien = cot.some((c) => c.doanh_thu > 0);
  const iDinh = cot.findIndex((c) => c.doanh_thu === dinh);

  // Cột càng ít thì cho càng rộng — 7 cột mảnh như que nằm giữa một khoảng trống
  // rộng trông như biểu đồ hỏng, chứ không như một tuần bán ít.
  const rong = theoGio ? 28 : cot.length <= 8 ? 56 : cot.length <= 12 ? 44 : 30;

  /*
    Nhãn dưới trục: viết hết thì chữ chồng lên nhau và không đọc được chữ nào.
      · theo giờ  → 3 giờ một nhãn (0h 3h 6h … 21h)
      · ≤ 10 cột  → viết hết
      · ≤ 31 cột  → 5 ngày một nhãn, nhưng LUÔN có ngày cuối
    Ngày cuối phải có: người xem cần biết biểu đồ chạy tới hôm nào.
  */
  const coNhan = (i: number): boolean => {
    if (theoGio) return i % 3 === 0;
    if (cot.length <= 10) return true;
    return i % 5 === 0 || i === cot.length - 1;
  };

  return (
    <div className="lom" style={{ padding: '18px 20px 12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 4,
          overflowX: 'auto',
        }}
      >
        {cot.map((c, i) => {
          const cao = Math.round((c.doanh_thu / dinh) * 100);
          return (
            <div
              key={c.khoa}
              title={c.chuThich}
              style={{ flex: '1 1 0', minWidth: 6, maxWidth: rong }}
            >
              <div
                style={{
                  height: 130,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
              >
                <div
                  style={{
                    // Khay không bán được gì vẫn để lại một vạch mờ — có mặt trên
                    // trục, chứ không biến mất.
                    height: `${Math.max(cao, c.doanh_thu > 0 ? 4 : 2)}%`,
                    borderRadius: 5,
                    background:
                      c.doanh_thu > 0 ? 'linear-gradient(180deg, #f4225f, #cc003e)' : 'var(--nen-3)',
                    boxShadow: c.doanh_thu > 0 ? '2px 2px 5px rgba(150,0,46,.28)' : 'none',
                    transition: 'height var(--cham)',
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 7,
                  fontSize: 9.5,
                  textAlign: 'center',
                  color: i === iDinh && coTien ? 'var(--nhan)' : 'var(--chu-3)',
                  fontWeight: i === iDinh && coTien ? 700 : 400,
                  whiteSpace: 'nowrap',
                  // Giữ chỗ cả khi không viết chữ, để mọi cột cao bằng nhau.
                  minHeight: 12,
                }}
              >
                {coNhan(i) || (i === iDinh && coTien) ? c.nhan : ''}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          // Không kẻ vạch ngăn: cả hệ giao diện này không dùng đường viền, chỗ
          // nào cần tách thì tách bằng khoảng trống và bóng.
          marginTop: 12,
          fontSize: 11,
          color: 'var(--chu-3)',
          textAlign: 'center',
        }}
      >
        {theoGio ? `24 giờ của ngày ${tu}` : `${cot.length} ngày · ${tu} → ${den}`}
        {coTien
          ? ` · đỉnh ${tien(dinh)} ${theoGio ? 'lúc' : 'ngày'} ${cot[iDinh]?.nhan}`
          : ' · chưa có doanh thu'}
      </div>
    </div>
  );
}

/**
 * 🔴 Tải file KHÔNG dùng thẻ `<a href>` được: endpoint đòi header
 *    `Authorization`, mà thẻ `a` không gửi header nào cả. Phải `fetch` rồi tạo
 *    blob. Nhớ `revokeObjectURL`, không thì giữ nguyên file zip trong bộ nhớ.
 */
function NutXuatExcel({ tu, den, choPhep }: { tu: string; den: string; choPhep: boolean }) {
  const [dangTai, datDangTai] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const tai = async () => {
    if (dangTai || !choPhep) return;
    datDangTai(true);
    datLoi(null);
    try {
      const r = await fetch(`/api/bao-cao/xuat-excel?tu=${tu}&den=${den}`, {
        headers: { Authorization: `Bearer ${layToken() ?? ''}` },
      });
      if (!r.ok) throw new Error(`Máy chủ trả lỗi ${r.status}`);
      const blob = await r.blob();
      const ten =
        r.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/)?.[1] ??
        `bao-cao-${tu}-den-${den}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ten;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      datLoi(e instanceof Error ? e.message : 'Không tải được.');
    } finally {
      datDangTai(false);
    }
  };

  return (
    <div style={{ textAlign: 'right' }}>
      <button className="nut-chinh" onClick={() => void tai()} disabled={dangTai || !choPhep}>
        {dangTai ? 'Đang gói…' : 'Tải Excel (.zip)'}
      </button>
      {loi && (
        <div style={{ fontSize: 11, color: 'var(--nhan)', fontWeight: 600, marginTop: 6 }}>{loi}</div>
      )}
    </div>
  );
}
